import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { saveTheme } from "../../actions/template";
import { prepareThemeImage } from "../../utils/themeImage";
import {
  ORIENTATIONS,
  fieldClass,
  labelClass,
  primaryButton,
  secondaryButton,
} from "./templateLabels";

// Adds a theme (no theme) or edits one. The picture is cropped to A4 here, so what
// the preview shows is what the PDF gets. The slug stays once saved: templates name
// the theme by it.
const EMPTY = {
  slug: "",
  name: "",
  group: "",
  orientation: "landscape",
  textColor: "#000000",
  accentColor: "#000000",
  order: "0",
};

const PARAM_LABELS = {
  slug: "Slug",
  name: "Нэр",
  group: "Бүлэг",
  orientation: "Чиглэл",
  textColor: "Текстийн өнгө",
  accentColor: "Гарчгийн өнгө",
  order: "Дараалал",
  image: "Зураг",
  thumbnail: "Жижиг зураг",
};

const ThemeForm = ({ theme, onClose, onSaved, saveTheme }) => {
  const isNew = !theme;
  const [form, setForm] = useState(
    isNew ? EMPTY : { ...EMPTY, ...theme, order: String(theme.order ?? 0) }
  );
  const [file, setFile] = useState(null);
  // { image, thumbnail } made from the file for the form's orientation
  const [pictures, setPictures] = useState(null);
  const [preview, setPreview] = useState(isNew ? null : theme.image);
  const [preparing, setPreparing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  // The picture is cropped again when the orientation changes
  useEffect(() => {
    if (!file) return undefined;
    let cancelled = false;
    let url = null;
    setPreparing(true);
    prepareThemeImage(file, form.orientation)
      .then((made) => {
        if (cancelled) return;
        url = URL.createObjectURL(made.image);
        setPictures(made);
        setPreview(url);
        setErrors([]);
      })
      .catch((err) => {
        if (cancelled) return;
        setPictures(null);
        setErrors([{ msg: err.message }]);
      })
      .finally(() => {
        if (!cancelled) setPreparing(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file, form.orientation]);

  const set = (name, value) => setForm({ ...form, [name]: value });
  const busy = saving || preparing;
  // A new theme needs a picture; a saved one keeps its own until a new one is chosen
  const turned = !isNew && !pictures && form.orientation !== theme.orientation;
  const canSave = !busy && form.name.trim() && (isNew ? form.slug.trim() && pictures : !turned);

  const close = () => {
    if (!busy) onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    const { slug, name, group, orientation, textColor, accentColor, order } = form;
    const result = await saveTheme(
      isNew ? null : theme.slug,
      { slug, name, group, orientation, textColor, accentColor, order },
      pictures
    );
    setSaving(false);
    if (result.errors) {
      setErrors(result.errors);
      return;
    }
    onSaved(result.theme);
  };

  const landscape = form.orientation === "landscape";

  return (
    // Only a press on the backdrop itself closes the sheet
    <div
      className="fixed inset-0 z-30 flex items-end pt-12 bg-black bg-opacity-50 sm:items-center sm:justify-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <form
        onSubmit={submit}
        className="flex flex-col w-full max-h-full overflow-hidden bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-2xl"
      >
        <header className="flex items-start justify-between flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <p className="text-lg font-semibold text-gray-700 truncate dark:text-gray-300">
            {isNew ? "Шинэ дизайн" : "Дизайн засах"}
          </p>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="inline-flex items-center justify-center w-6 h-6 ml-3 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover:text-gray-700"
            aria-label="close"
          >
            <i className="fas fa-times" />
          </button>
        </header>

        <div className="min-h-0 px-6 py-4 space-y-4 overflow-y-auto">
          {errors.length > 0 && (
            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md dark:bg-red-900 dark:text-red-100">
              {errors.map((error, i) => (
                <p key={i}>
                  {PARAM_LABELS[error.param] ? PARAM_LABELS[error.param] + ": " : ""}
                  {error.msg}
                </p>
              ))}
            </div>
          )}

          <div
            className="relative overflow-hidden bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600"
            style={{ aspectRatio: landscape ? "297 / 210" : "210 / 297", maxHeight: 360, margin: "0 auto" }}
          >
            {preview ? (
              <img src={preview} alt="" className="block object-cover w-full h-full" />
            ) : (
              <div className="flex items-center justify-center w-full h-full p-6 text-sm text-center text-gray-500 dark:text-gray-400">
                A4 {landscape ? "хэвтээ" : "босоо"} зураг. Дунд хэсэг нь текстэнд хоосон, чимэглэл нь захаараа байвал сайн.
              </div>
            )}
            {preparing && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-white bg-black bg-opacity-40">
                Зургийг бэлдэж байна...
              </div>
            )}
          </div>

          <label className="block">
            <span className={labelClass}>Зураг (JPEG, PNG)</span>
            <input
              type="file"
              accept="image/jpeg,image/png"
              disabled={saving}
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-700 dark:text-gray-300"
            />
            <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
              A4-т тааруулж захыг нь тайрна. 1754×1240 (хэвтээ) эсвэл 1240×1754 (босоо) px бол тайрахгүй.
              Хэвлэгч цаасны ирмэг хүртэл хэвлэдэггүй: захаас 5 мм-т чухал зүйл бүү тавь.
            </span>
          </label>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className={labelClass}>Нэр</span>
              <input
                className={fieldClass}
                value={form.name}
                maxLength={60}
                onChange={(e) => set("name", e.target.value)}
                disabled={saving}
                placeholder="Сагсан бөмбөг"
              />
            </label>
            <label className="block">
              <span className={labelClass}>Slug</span>
              <input
                className={fieldClass}
                value={form.slug}
                maxLength={60}
                onChange={(e) => set("slug", e.target.value)}
                disabled={saving || !isNew}
                placeholder="sags-ulaan"
              />
              <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isNew ? "Латин жижиг үсэг, цифр, зураас. Дараа нь өөрчлөхгүй." : "Загварууд үүгээр нэрлэдэг тул өөрчлөхгүй."}
              </span>
            </label>
            <label className="block">
              <span className={labelClass}>Бүлэг</span>
              <input
                className={fieldClass}
                value={form.group}
                maxLength={40}
                onChange={(e) => set("group", e.target.value)}
                disabled={saving}
                placeholder="Спорт"
              />
            </label>
            <label className="block">
              <span className={labelClass}>Хуудас (A4)</span>
              <select
                className={fieldClass + " pr-8"}
                value={form.orientation}
                onChange={(e) => set("orientation", e.target.value)}
                disabled={saving}
              >
                {ORIENTATIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {turned && (
                <span className="block mt-1 text-xs text-red-600 dark:text-red-400">
                  Чиглэл солиход шинэ зураг сонгоно уу.
                </span>
              )}
            </label>
            <label className="block">
              <span className={labelClass}>Текстийн өнгө</span>
              <input
                type="color"
                className="block w-full h-10 p-1 border border-gray-300 rounded-lg dark:border-gray-600"
                value={form.textColor}
                onChange={(e) => set("textColor", e.target.value)}
                disabled={saving}
              />
            </label>
            <label className="block">
              <span className={labelClass}>Гарчгийн өнгө</span>
              <input
                type="color"
                className="block w-full h-10 p-1 border border-gray-300 rounded-lg dark:border-gray-600"
                value={form.accentColor}
                onChange={(e) => set("accentColor", e.target.value)}
                disabled={saving}
              />
            </label>
            <label className="block">
              <span className={labelClass}>Дараалал</span>
              <input
                type="number"
                className={fieldClass}
                value={form.order}
                onChange={(e) => set("order", e.target.value)}
                disabled={saving}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Загварын агуулгад {"style=\"color:{{theme_text}}\""} болон {"{{theme_accent}}"} гэж бичсэн хэсэг эдгээр өнгийг авна.
          </p>
        </div>

        <footer className="flex justify-end flex-shrink-0 gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={secondaryButton} onClick={close} disabled={busy}>
            Болих
          </button>
          <button
            type="submit"
            className={primaryButton + (canSave ? "" : " opacity-50 cursor-not-allowed")}
            disabled={!canSave}
          >
            {saving ? "Хадгалж байна..." : "Хадгалах"}
          </button>
        </footer>
      </form>
    </div>
  );
};

ThemeForm.propTypes = {
  theme: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  saveTheme: PropTypes.func.isRequired,
};

export default connect(null, { saveTheme })(ThemeForm);
